import React from 'react';
import { useTopCategoriesQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { TopCategories } from '@/api/models';
import { useSales } from '@/pages/sales/SalesProvider';

/**
 * Returns a `TopCategories` snapshot for the widget, scoped to the
 * page-level date range published through `SalesProvider`. Changing
 * the period filter on the page re-runs the underlying query because
 * `useTopCategoriesQuery` keys on `from` / `to`.
 *
 * Mapping rules mirror the other Sales hooks.
 */
export const useCategoryAnalysis = () => {
    const { dateRange } = useSales();
    const query = useTopCategoriesQuery({
        from: dateRange.start,
        to: dateRange.end,
    });
    const { refetch } = query;

    const data = React.useMemo<TopCategories | null>(
        () => (query.data?.success ? query.data.response : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    return {
        data,
        error,
        isLoading: query.isPending,
        onRetry,
    } as const;
};
